/**
 * Child day view.
 * Used on child devices AND as a read-only preview for authenticated parents.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { ChildAddDrawer } from "@/components/child/ChildAddDrawer";
import { useParams, Navigate, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useChildDeviceSession } from "@/hooks/useChildDeviceSession";
import { useDeviceHashes } from "@/hooks/useDeviceHashes";
import { useAuth } from "@/hooks/useAuth";
import { useTimer } from "@/hooks/useTimer";
import { CircularTimer } from "@/components/CircularTimer";
import { ChildDayViewSkeleton } from "@/components/skeletons/PageSkeletons";
import { formatDateLong, getDateString, getDayRange } from "@/lib/family-date";

import { HomeworkSection } from "@/components/HomeworkSection";
import { ReminderSection } from "@/components/ReminderSection";
import { ChildWeekView } from "@/components/child/ChildWeekView";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
  DrawerClose,
} from "@/components/ui/drawer";
import { Slider } from "@/components/ui/slider";
import confetti from "canvas-confetti";
import { ChevronLeft, Home, CalendarDays, Circle, CheckCircle2, Plus, Play } from "lucide-react";
import { PwaUpdatePrompt } from "@/components/PwaUpdatePrompt";
import { useCustodyForDate } from "@/hooks/useTodayCustody";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type ViewState =
  | { type: "list" }
  | { type: "timer"; itemId: string; title: string; description?: string | null; isRecurring?: boolean }
  | { type: "done"; itemId: string; title: string; isRecurring?: boolean }
  | { type: "improvement"; itemId: string; title: string; isRecurring?: boolean }
  | { type: "celebration"; itemId: string; title: string };

export type ChildDayViewProps = {
  /** When true, view is read-only parent preview (1:1 UI with child view) */
  previewMode?: boolean;
  /** When set, parent preview back button navigates here instead of history back (e.g. "/dashboard") */
  previewBackTo?: string;
};

export default function ChildDayView({ previewMode, previewBackTo }: ChildDayViewProps = {}) {
  const { id: childId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const deviceSession = useChildDeviceSession();
  const deviceHashes = useDeviceHashes();
  const queryClient = useQueryClient();
  const [view, setView] = useState<ViewState>({ type: "list" });
  const [newTitle, setNewTitle] = useState("");
  const [improvementNote, setImprovementNote] = useState("");
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [addDrawerOpen, setAddDrawerOpen] = useState(false);
  const [drawerDuration, setDrawerDuration] = useState(10);
  const [progressOpen, setProgressOpen] = useState(false);
  const remainingSecondsRef = useRef<Record<string, number>>({});
  const [activeItemDuration, setActiveItemDuration] = useState(600);
  const [confirmStartItem, setConfirmStartItem] = useState<{
    id: string;
    title: string;
    duration: number;
    description?: string | null;
    isRecurring?: boolean;
  } | null>(null);
  const prevItemStatusesRef = useRef<Record<string, string>>({});
  const celebrationSeenLocalRef = useRef<Set<string>>(new Set());

  const isChildDevice = deviceSession.isChildDevice && deviceSession.childId === childId;
  const accessLoading = deviceSession.isLoading;

  // Parent preview: explicit previewMode from parent, or inferred when parent opens /child/:id
  const isParentPreview =
    previewMode === true || (!authLoading && !!user && !isChildDevice);

  // Set data-role on body
  useEffect(() => {
    if (!isParentPreview) {
      document.body.setAttribute("data-role", "child");
      return () => {
        document.body.removeAttribute("data-role");
      };
    }
  }, [isParentPreview]);

  useEffect(() => {
    if (!authLoading && !accessLoading && !isChildDevice && !user) {
      setShouldRedirect(true);
    }
  }, [accessLoading, authLoading, isChildDevice, user]);

  // Use Europe/Oslo as default for child device (family timezone not easily available without auth)
  const timezone = "Europe/Oslo";
  const realToday = getDateString(new Date(), timezone);

  // Parent preview: allow switching date
  const [previewDate, setPreviewDate] = useState<string | null>(null);
  const today = isParentPreview && previewDate ? previewDate : realToday;
  const previewDateObj = previewDate ? new Date(previewDate + "T12:00:00") : undefined;

  const { start: dayStart, end: dayEnd } = getDayRange(
    previewDate ? new Date(previewDate + "T12:00:00") : new Date(),
    timezone,
  );

  // Custody check for parent preview
  const { isWithOtherParent } = useCustodyForDate(today);
  const childWithOtherParent = isParentPreview && !!childId && isWithOtherParent(childId);

  // ── Child profile ──────────────────────────────────
  const { data: child } = useQuery({
    queryKey: ["child", childId, isParentPreview ? "parent-preview" : "device"],
    queryFn: async () => {
      if (!childId) return null;
      if (isParentPreview) {
        const { data, error } = await supabase
          .from("children")
          .select("id, name, avatar_emoji, family_id")
          .eq("id", childId)
          .single();
        if (error) throw error;
        return data;
      }
      if (!deviceHashes) return null;
      const { data, error } = await supabase.rpc("get_child_for_device", {
        _device_id_hash: deviceHashes.deviceIdHash,
        _session_token_hash: deviceHashes.sessionTokenHash,
      });
      if (error) throw error;
      return data && data.length > 0 ? data[0] : null;
    },
    enabled: !!childId && (isParentPreview || (isChildDevice && !!deviceHashes)),
  });

  // ── Focus items ──────────────────────────────────
  const deviceItemsKey = ["task-items-device", childId, today, isParentPreview ? "preview" : "device"];

  const { data: items = [] } = useQuery({
    queryKey: deviceItemsKey,
    queryFn: async () => {
      if (isParentPreview) {
        const { data, error } = await supabase
          .from("task_items")
          .select("*")
          .eq("child_id", childId!)
          .eq("date", today)
          .order("created_at", { ascending: true });
        if (error) throw error;
        return data ?? [];
      }
      if (!deviceHashes) return [];
      const { data, error } = await supabase.rpc("get_task_items_for_device", {
        _device_id_hash: deviceHashes.deviceIdHash,
        _session_token_hash: deviceHashes.sessionTokenHash,
        _date: today,
      });
      if (error) throw error;
      return data ?? [];
    },
    enabled: isParentPreview || (isChildDevice && !!deviceHashes),
    refetchInterval: isParentPreview ? undefined : 5000,
  });

  // ── Recurring task occurrences ──────────────────────
  const recurringKey = ["task-occurrences-device", childId, today, isParentPreview ? "preview" : "device"];

  const { data: recurringItems = [] } = useQuery({
    queryKey: recurringKey,
    queryFn: async () => {
      if (isParentPreview) {
        // Parent preview: direct table query via RLS
        const { data: occs, error: occErr } = await supabase
          .from("task_occurrences")
          .select("id, template_id, due_date, status, title_override, started_at, done_at, improvement_note")
          .eq("due_date", today)
          .neq("status", "skipped");
        if (occErr) throw occErr;
        if (!occs || occs.length === 0) return [];

        const templateIds = [...new Set(occs.map((o) => o.template_id))];
        const { data: templates, error: tplErr } = await supabase
          .from("task_templates")
          .select("id, title, description, category, duration_seconds, completion_points, child_id")
          .in("id", templateIds)
          .eq("child_id", childId!);
        if (tplErr) throw tplErr;

        const tplMap = new Map((templates ?? []).map((t) => [t.id, t]));
        return occs
          .filter((o) => tplMap.has(o.template_id))
          .map((o) => {
            const tpl = tplMap.get(o.template_id)!;
            return {
              id: o.id,
              template_id: o.template_id,
              title: o.title_override || tpl.title,
              description: tpl.description,
              category: tpl.category,
              duration_seconds: tpl.duration_seconds,
              completion_points: tpl.completion_points,
              status: o.status === "done" ? "done" : o.started_at ? "active" : "pending",
              started_at: o.started_at,
              done_at: o.done_at,
              improvement_note: o.improvement_note,
              isRecurring: true as const,
            };
          });
      }
      if (!deviceHashes) return [];
      const { data, error } = await supabase.rpc("get_task_occurrences_for_device", {
        _device_id_hash: deviceHashes.deviceIdHash,
        _session_token_hash: deviceHashes.sessionTokenHash,
        _date: today,
      });
      if (error) throw error;
      return (data ?? []).map((o: any) => ({
        ...o,
        status: o.status === "done" ? "done" : o.started_at ? "active" : "pending",
        isRecurring: true as const,
      }));
    },
    enabled: isParentPreview || (isChildDevice && !!deviceHashes),
    refetchInterval: isParentPreview ? undefined : 5000,
  });

  // JIT materialize occurrences for current date (child device)
  const jitRef = useRef<string>("");
  useEffect(() => {
    if (isParentPreview || !childId || !deviceHashes) return;
    const key = `${childId}-${today}`;
    if (jitRef.current === key) return;
    jitRef.current = key;
    supabase.functions
      .invoke("create-daily-occurrences", {
        body: { child_id: childId, start_date: today, end_date: today, include: ["tasks"] },
      })
      .then(({ error }) => {
        if (error) console.warn("JIT ensure failed:", error);
        else queryClient.invalidateQueries({ queryKey: recurringKey });
      });
  }, [childId, today, deviceHashes, isParentPreview, queryClient]);

  // Merge one-off + recurring into a single list for rendering
  const allItems = [
    ...items.map((i: any) => ({ ...i, isRecurring: false })),
    ...recurringItems,
  ];

  useEffect(() => {
    if (isParentPreview) return;
    if (!childId) return;
    const channel = supabase
      .channel(`task-device-${childId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "task_items", filter: `child_id=eq.${childId}` },
        () => {
          queryClient.invalidateQueries({ queryKey: deviceItemsKey });
          queryClient.invalidateQueries({ queryKey: ["points-device", childId, today] });
        },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "task_occurrences" },
        () => {
          queryClient.invalidateQueries({ queryKey: recurringKey });
          queryClient.invalidateQueries({ queryKey: ["points-device", childId, today] });
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [childId, today, queryClient, isParentPreview]);

  // ── Mutations (disabled in parent preview) ─────────
  const deviceAddItem = useMutation({
    mutationFn: async (title: string) => {
      if (isParentPreview) throw new Error("Read-only preview");
      if (!deviceHashes) throw new Error("No device hashes");
      const { data, error } = await supabase.rpc("add_task_item_for_device", {
        _device_id_hash: deviceHashes.deviceIdHash,
        _session_token_hash: deviceHashes.sessionTokenHash,
        _title: title,
        _date: today,
        _duration_seconds: drawerDuration * 60,
        _completion_points: 5,
      });
      if (error) throw error;
      // data is the new task id (uuid)
      return { id: data as string, title };
    },
    onSuccess: (result: { id: string; title: string }) => {
      // Classify in background
      supabase.functions
        .invoke("classify-task", {
          body: { taskId: result.id, title: result.title },
        })
        .catch((err) => console.warn("Task classification failed:", err));
      queryClient.invalidateQueries({ queryKey: deviceItemsKey });
    },
    onError: (err: any) => {
      const msg = err?.message || "";
      if (msg.includes("Max 3")) {
        toast.error("Du har allerede 3 ting for i dag");
      } else if (msg.includes("Invalid device session")) {
        toast.error("Enheten er ikke lenger koblet. Prøv å koble på nytt.");
      } else {
        toast.error("Kunne ikke legge til. Prøv igjen.");
        console.error("[ChildDayView] addItem error:", err);
      }
    },
  });

  const deviceStartItem = useMutation({
    mutationFn: async (itemId: string) => {
      if (isParentPreview) throw new Error("Read-only preview");
      if (!deviceHashes) throw new Error("No device hashes");
      const { error } = await supabase.rpc("start_task_item_for_device", {
        _device_id_hash: deviceHashes.deviceIdHash,
        _session_token_hash: deviceHashes.sessionTokenHash,
        _item_id: itemId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deviceItemsKey });
      queryClient.invalidateQueries({ queryKey: ["points-device", childId, today] });
    },
  });

  const deviceCompleteItem = useMutation({
    mutationFn: async ({ itemId, improvementNote }: { itemId: string; improvementNote?: string }) => {
      if (isParentPreview) throw new Error("Read-only preview");
      if (!deviceHashes) throw new Error("No device hashes");
      const { error } = await supabase.rpc("complete_task_item_for_device", {
        _device_id_hash: deviceHashes.deviceIdHash,
        _session_token_hash: deviceHashes.sessionTokenHash,
        _item_id: itemId,
        _improvement_note: improvementNote || null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deviceItemsKey });
      queryClient.invalidateQueries({ queryKey: ["points-device", childId, today] });
      queryClient.invalidateQueries({ queryKey: ["points-total-device", childId] });
    },
  });

  // ── Recurring task mutations ─────────────────────
  const deviceStartOccurrence = useMutation({
    mutationFn: async (occurrenceId: string) => {
      if (isParentPreview) throw new Error("Read-only preview");
      if (!deviceHashes) throw new Error("No device hashes");
      const { error } = await supabase.rpc("start_task_occurrence_for_device", {
        _device_id_hash: deviceHashes.deviceIdHash,
        _session_token_hash: deviceHashes.sessionTokenHash,
        _occurrence_id: occurrenceId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: recurringKey });
    },
  });

  const deviceCompleteOccurrence = useMutation({
    mutationFn: async ({ occurrenceId, improvementNote }: { occurrenceId: string; improvementNote?: string }) => {
      if (isParentPreview) throw new Error("Read-only preview");
      if (!deviceHashes) throw new Error("No device hashes");
      const { error } = await supabase.rpc("complete_task_occurrence_for_device", {
        _device_id_hash: deviceHashes.deviceIdHash,
        _session_token_hash: deviceHashes.sessionTokenHash,
        _occurrence_id: occurrenceId,
        _improvement_note: improvementNote || null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: recurringKey });
      queryClient.invalidateQueries({ queryKey: ["points-device", childId, today] });
      queryClient.invalidateQueries({ queryKey: ["points-total-device", childId] });
    },
  });

  // ── Points ──────────────────────────────────────────
  const { data: pointsToday = 0 } = useQuery({
    queryKey: ["points-device", childId, today, isParentPreview ? "preview" : "device"],
    queryFn: async () => {
      if (isParentPreview) {
        const { data, error } = await supabase
          .from("point_transactions")
          .select("amount")
          .eq("child_id", childId!)
          .gte("created_at", dayStart)
          .lte("created_at", dayEnd);
        if (error) throw error;
        return (data ?? []).reduce((sum: number, r: any) => sum + r.amount, 0);
      }
      if (!deviceHashes) return 0;
      const { data, error } = await supabase.rpc("get_points_today_for_device", {
        _device_id_hash: deviceHashes.deviceIdHash,
        _session_token_hash: deviceHashes.sessionTokenHash,
        _start_ts: dayStart,
        _end_ts: dayEnd,
      });
      if (error) throw error;
      return data ?? 0;
    },
    enabled: isParentPreview || (isChildDevice && !!deviceHashes),
    refetchInterval: isParentPreview ? undefined : 5000,
  });

  // ── Total points (all-time) ────────────────────────
  const { data: pointsTotal = 0 } = useQuery({
    queryKey: ["points-total-device", childId, isParentPreview ? "preview" : "device"],
    queryFn: async () => {
      if (isParentPreview) {
        const { data, error } = await supabase.from("point_transactions").select("amount").eq("child_id", childId!);
        if (error) throw error;
        return (data ?? []).reduce((sum: number, r: any) => sum + r.amount, 0);
      }
      if (!deviceHashes) return 0;
      const { data, error } = await supabase.rpc("get_points_today_for_device", {
        _device_id_hash: deviceHashes.deviceIdHash,
        _session_token_hash: deviceHashes.sessionTokenHash,
        _start_ts: "2020-01-01T00:00:00Z",
        _end_ts: "2099-12-31T23:59:59Z",
      });
      if (error) throw error;
      return data ?? 0;
    },
    enabled: isParentPreview || (isChildDevice && !!deviceHashes),
    refetchInterval: isParentPreview ? undefined : 15000,
  });

  const canAdd = !isParentPreview && items.length < 3;
  const timer = useTimer(activeItemDuration);

  // Detect completed items the child hasn't seen yet → show celebration
  // Uses database column celebration_seen_at for cross-device persistence
  // Also tracks locally via celebrationSeenLocalRef to avoid loop while DB update propagates
  // When on list: any done/unseen item can trigger celebration. When on timer: only the current timer task.
  useEffect(() => {
    if (isParentPreview) return; // No celebrations in preview
    if (!items || items.length === 0 || !deviceHashes) return;
    if (view.type !== "list" && view.type !== "timer") return;

    const showCelebration = (item: { id: string; title: string }) => {
      celebrationSeenLocalRef.current.add(item.id);
      supabase
        .rpc("mark_celebration_seen_for_device", {
          _device_id_hash: deviceHashes.deviceIdHash,
          _session_token_hash: deviceHashes.sessionTokenHash,
          _item_id: item.id,
        })
        .then(() => {
          queryClient.invalidateQueries({ queryKey: deviceItemsKey });
        });
      if (view.type === "timer") {
        delete remainingSecondsRef.current[view.itemId];
        timer.pause();
      }
      setView({ type: "celebration", itemId: item.id, title: item.title });
      confetti({
        particleCount: 120,
        spread: 80,
        origin: { y: 0.6 },
        colors: ["#FFD700", "#FF6B6B", "#4ECDC4", "#45B7D1", "#96CEB4"],
      });
    };

    if (view.type === "timer") {
      // Only trigger if the task the kid is timing is the one parent marked done
      const currentItem = (items as any[]).find((i: any) => i.id === view.itemId);
      if (
        currentItem &&
        currentItem.status === "done" &&
        !currentItem.celebration_seen_at &&
        !celebrationSeenLocalRef.current.has(currentItem.id)
      ) {
        showCelebration(currentItem);
      }
      return;
    }

    for (const item of items as any[]) {
      if (item.status === "done" && !item.celebration_seen_at && !celebrationSeenLocalRef.current.has(item.id)) {
        showCelebration(item);
        break;
      }
    }
  }, [items, view.type, view.type === "timer" ? (view as any).itemId : undefined, deviceHashes, isParentPreview, timer, deviceItemsKey, queryClient]);

  if (!isParentPreview && accessLoading) {
    return <ChildDayViewSkeleton />;
  }

  if (shouldRedirect) {
    return <Navigate to="/child/access" replace />;
  }

  // ── Handlers (no-ops in preview) ──────────────────
  function handleAddItem() {
    if (isParentPreview) return;
    if (!newTitle.trim()) return;
    deviceAddItem.mutate(newTitle.trim(), {
      onSuccess: () => {
        setNewTitle("");
        setDrawerDuration(10);
        setAddDrawerOpen(false);
      },
    });
  }

  function handleStart(itemId: string, title: string, durationSeconds: number = 600, description?: string | null, isRecurring?: boolean) {
    if (isParentPreview) return;
    const startMutation = isRecurring ? deviceStartOccurrence : deviceStartItem;
    startMutation.mutate(itemId, {
      onSuccess: () => {
        setActiveItemDuration(durationSeconds);
        setView({ type: "timer", itemId, title, description, isRecurring });
        const saved = remainingSecondsRef.current[itemId];
        timer.start(saved && saved > 0 ? saved : durationSeconds);
      },
    });
  }

  function handleResume(itemId: string, title: string, durationSeconds: number = 600, description?: string | null, isRecurring?: boolean) {
    if (isParentPreview) return;
    setActiveItemDuration(durationSeconds);
    setView({ type: "timer", itemId, title, description, isRecurring });
    const saved = remainingSecondsRef.current[itemId];
    timer.start(saved && saved > 0 ? saved : durationSeconds);
  }

  function handlePause(itemId: string) {
    remainingSecondsRef.current[itemId] = timer.secondsLeft;
    timer.pause();
    setView({ type: "list" });
  }

  function handleComplete(itemId: string, title: string) {
    if (isParentPreview) return;
    const isRec = allItems.find((i: any) => i.id === itemId)?.isRecurring;
    // Mark celebration as seen in DB so other devices won't show it again (only for task_items)
    if (deviceHashes && !isRec) {
      supabase.rpc("mark_celebration_seen_for_device", {
        _device_id_hash: deviceHashes.deviceIdHash,
        _session_token_hash: deviceHashes.sessionTokenHash,
        _item_id: itemId,
      });
    }
    setView({ type: "improvement", itemId, title, isRecurring: isRec });
  }

  function handleSaveImprovement(itemId: string) {
    if (isParentPreview) return;
    const item = allItems.find((i: any) => i.id === itemId);
    const isRec = item?.isRecurring;
    const cat = (item as any)?.category;
    const pts = cat && cat !== "Annet" ? 1 : 0;
    const onSuccess = () => {
      toast.success(pts > 0 ? "Bra jobba! ⭐ +1" : "Bra jobba!");
      setImprovementNote("");
      timer.reset();
      setView({ type: "list" });
    };
    if (isRec) {
      deviceCompleteOccurrence.mutate(
        { occurrenceId: itemId, improvementNote: improvementNote.trim() || undefined },
        { onSuccess },
      );
    } else {
      deviceCompleteItem.mutate(
        { itemId, improvementNote: improvementNote.trim() || undefined },
        { onSuccess },
      );
    }
  }

  function handleSkipImprovement(itemId: string) {
    if (isParentPreview) return;
    const item = allItems.find((i: any) => i.id === itemId);
    const isRec = item?.isRecurring;
    const cat = (item as any)?.category;
    const pts = cat && cat !== "Annet" ? 1 : 0;
    const onSuccess = () => {
      toast.success(pts > 0 ? "Bra jobba! ⭐ +1" : "Bra jobba!");
      timer.reset();
      setView({ type: "list" });
    };
    if (isRec) {
      deviceCompleteOccurrence.mutate({ occurrenceId: itemId }, { onSuccess });
    } else {
      deviceCompleteItem.mutate({ itemId }, { onSuccess });
    }
  }

  // ── Timer view (not in preview) ────────────────────
  if (view.type === "timer" && !isParentPreview) {
    return (
      <ChildTimerView
        view={view}
        timer={timer}
        setView={setView}
        onPause={handlePause}
        onComplete={handleComplete}
        remainingSecondsRef={remainingSecondsRef}
        durationSeconds={activeItemDuration}
      />
    );
  }

  // ── Celebration view (parent completed) ─────────────
  if (view.type === "celebration" && !isParentPreview) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4 gap-6 animate-fade-in">
        <div className="text-7xl animate-scale-in">🏆</div>
        <h2 className="text-2xl font-display font-bold text-center">Gratulerer!</h2>
        <p className="text-muted-foreground text-center text-lg">«{view.title}» er godkjent! 🎉</p>
        <p className="text-sm text-muted-foreground text-center">Foreldrene dine er stolte av deg! ⭐</p>
        <Button onClick={() => setView({ type: "list" })} className="w-full max-w-xs rounded-xl text-base py-5">
          Tilbake til oppgavene
        </Button>
      </div>
    );
  }

  // ── Done view (child completed) ─────────────────────
  if (view.type === "done" && !isParentPreview) {
    return <DoneWithConfetti title={view.title} onContinue={() => handleComplete(view.itemId, view.title)} />;
  }

  // ── Improvement view ──────────────────────────────
  if (view.type === "improvement" && !isParentPreview) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4 gap-6">
        <h2 className="text-xl font-display font-bold text-center">Neste gang</h2>
        <p className="text-sm text-muted-foreground text-center">Hva vil du gjøre litt bedre neste gang?</p>
        <Textarea
          value={improvementNote}
          onChange={(e) => setImprovementNote(e.target.value)}
          placeholder="Skriv her… (valgfritt)"
          className="w-full max-w-xs"
          rows={3}
        />
        <div className="flex gap-2 w-full max-w-xs">
          <Button variant="outline" className="flex-1" onClick={() => handleSkipImprovement(view.itemId)}>
            Hopp over
          </Button>
          <Button className="flex-1" onClick={() => handleSaveImprovement(view.itemId)}>
            Lagre
          </Button>
        </div>
      </div>
    );
  }

  // ── Progress ──────────────────────────────────────
  const allDone = allItems.length > 0 && allItems.every((i: any) => i.status === "done");

  // ── List view ──────────────────────────────────────
  return (
    <div
      className="relative min-h-[100dvh]"
      style={{ background: "hsl(var(--secondary))" }}
    >
      {/* Full-bleed gradient; container matches gradient top so overscroll/overlap is never white */}
      <div
        className="fixed inset-0 pointer-events-none z-[1]"
        style={{
          background:
            "linear-gradient(to bottom, hsl(var(--secondary)) 0%, hsl(var(--secondary) / 0.7) 25%, hsl(var(--secondary) / 0.3) 50%, hsl(var(--background)) 100%)",
        }}
      />
      <PwaUpdatePrompt />
      <div className="relative z-[2] mx-auto max-w-sm space-y-4 px-3 py-4 pb-24">
        {/* Parent preview: compact sticky bar so main content matches child view */}
        {isParentPreview && (
          <div className="sticky top-0 z-10 -mx-3 -mt-6 mb-2 flex items-center gap-2 flex-wrap rounded-b-lg bg-secondary/20 backdrop-blur-sm px-3 py-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2 -ml-1"
              onClick={() => (previewBackTo ? navigate(previewBackTo) : navigate(-1))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
              👀 Forhåndsvisning
            </Badge>
            <div className="ml-auto">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-7 gap-1 text-xs rounded-full">
                    <CalendarDays className="h-3 w-3" />
                    {previewDate && previewDate !== realToday ? formatDateLong(previewDate, timezone) : "I dag"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <Calendar
                    mode="single"
                    selected={previewDateObj}
                    onSelect={(d) => {
                      if (d) {
                        setPreviewDate(getDateString(d, timezone));
                      } else {
                        setPreviewDate(null);
                      }
                    }}
                    className="p-3 pointer-events-auto"
                  />
                  {previewDate && previewDate !== realToday && (
                    <div className="px-3 pb-3">
                      <Button variant="ghost" size="sm" className="w-full text-xs" onClick={() => setPreviewDate(null)}>
                        Tilbake til i dag
                      </Button>
                    </div>
                  )}
                </PopoverContent>
              </Popover>
            </div>
          </div>
        )}

        {/* Custody: child is with other parent */}
        {childWithOtherParent && (
          <div className="rounded-2xl bg-muted/60 p-6 text-center space-y-2">
            <Home className="h-8 w-8 mx-auto text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {child?.name || "Barnet"} er hos den andre forelderen i dag. Oppgavene som vises her er begrenset til det
              du har tilgang til.
            </p>
          </div>
        )}

        {/* Header with gradient background */}
        <div className="text-center space-y-0.5 pb-2">
          <div>
            <div className="text-4xl mb-1">{child?.avatar_emoji || "🧒"}</div>
            <h1 className="text-2xl font-bold">Hei {child?.name || "der"} 👋</h1>
            <p className="text-sm text-muted-foreground capitalize">
              {formatDateLong(previewDate ? new Date(previewDate + "T12:00:00") : new Date(), timezone)}
            </p>
            <button
              onClick={() => setProgressOpen(true)}
              className={`mt-2 inline-flex flex-col items-center gap-0 rounded-2xl px-4 py-2 transition-colors active:scale-95 ${
                pointsToday > 0 ? "bg-accent/15 animate-soft-glow" : "bg-accent/10 hover:bg-accent/20"
              } ${isParentPreview ? "cursor-default" : ""}`}
            >
              <span className="text-sm font-semibold">⭐ {pointsToday} poeng i dag</span>
              <span className="text-[11px] text-muted-foreground">
                {pointsToday === 0 ? "Start med én ting ✨" : "Du er i gang"}
              </span>
            </button>
          </div>
        </div>

        {/* Focus items */}
        <div className="space-y-3 rounded-2xl bg-card/60 p-2 -mx-1 border-0 shadow-sm mt-1">
          <h2 className="text-base font-display font-bold flex items-center gap-2">I dag vil jeg få til</h2>

          {allItems.map((item: any) => {
            const isDone = item.status === "done";
            const isActive = item.status === "active";
            const isPending = item.status === "pending";
            return (
              <Card
                key={item.id}
                className={`border-0 transition-transform active:scale-[0.99] ${
                  isDone
                    ? "bg-[hsl(var(--success))]/5 shadow-sm"
                    : isActive
                      ? "bg-primary/5 ring-1 ring-primary/20 shadow-md"
                      : "bg-card hover:shadow-md shadow-sm"
                }`}
              >
                <CardContent className="flex items-center justify-between p-4 gap-3">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="shrink-0 relative">
                      {isDone ? (
                        <CheckCircle2 className="h-6 w-6 text-[hsl(var(--success))]" />
                      ) : isActive ? (
                        <span className="relative flex h-6 w-6 items-center justify-center">
                          <Circle className="h-6 w-6 text-primary" />
                          <span className="absolute inset-0 flex items-center justify-center">
                            <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
                          </span>
                        </span>
                      ) : (
                        <Circle className="h-6 w-6 text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`font-semibold ${isDone ? "text-muted-foreground line-through" : ""}`}>
                        {item.title}
                        {item.isRecurring && <span className="ml-1 text-xs text-muted-foreground">🔁</span>}
                      </p>
                      {!isDone && item.description && (
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{item.description}</p>
                      )}
                      {isDone && item.improvement_note && (
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                          💭 {item.improvement_note}
                        </p>
                      )}
                      {!isDone && (
                        <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5">
                          {isPending && (
                            <>
                              Klar?
                              {item.duration_seconds && ` · ${Math.round(item.duration_seconds / 60)} min`}
                            </>
                          )}
                          {isActive && (
                            <span className="inline-flex items-center gap-1 text-xs text-primary">
                              <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
                              Pågår
                            </span>
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                  {!isParentPreview && isPending && (
                    <Button
                      size="sm"
                      className="rounded-xl px-5 font-bold gap-1.5 active:scale-95 transition-transform"
                      onClick={() =>
                        setConfirmStartItem({
                          id: item.id,
                          title: item.title,
                          duration: item.duration_seconds,
                          description: item.description,
                          isRecurring: item.isRecurring,
                        })
                      }
                    >
                      Start <Play className="h-4 w-4" />
                    </Button>
                  )}
                  {!isParentPreview && isActive && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-xl px-5 font-bold gap-1.5 border-primary/40 text-primary active:scale-95 transition-transform"
                      onClick={() => handleResume(item.id, item.title, item.duration_seconds, item.description, item.isRecurring)}
                    >
                      Fortsett <Play className="h-4 w-4" />
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}

          {allItems.length === 0 && (
            <div className="text-center py-8 space-y-3 animate-bounce-in">
              <div className="text-5xl">🚀</div>
              <p className="text-base font-semibold">Hva vil du få til i dag?</p>
              <p className="text-sm text-muted-foreground">
                {isParentPreview ? "Ingen oppgaver for i dag" : "Legg til noe du vil få til 👇"}
              </p>
            </div>
          )}
          {/* Add new (not in preview) */}
          {!isParentPreview && canAdd ? (
            <>
              <Button
                className="w-full rounded-xl text-base py-6 font-bold shadow-md"
                onClick={() => setAddDrawerOpen(true)}
              >
                <Plus className="h-5 w-5 mr-1" /> Legg til noe nytt
              </Button>
              <ChildAddDrawer
                open={addDrawerOpen}
                onOpenChange={setAddDrawerOpen}
                onAdd={(title, durationMinutes) => {
                  setNewTitle(title);
                  setDrawerDuration(durationMinutes);
                  deviceAddItem.mutate(title, {
                    onSuccess: () => {
                      setNewTitle("");
                      setDrawerDuration(10);
                      setAddDrawerOpen(false);
                    },
                  });
                }}
                isPending={deviceAddItem.isPending}
              />
            </>
          ) : !isParentPreview && !allDone ? (
            <p className="text-center text-sm text-muted-foreground bg-muted rounded-lg p-3 mt-2">
              Du har allerede 3 ting for i dag 🎯
            </p>
          ) : null}
        </div>

        {/* Reminders section — show before homework */}
        {childId && (
          <ReminderSection
            childId={childId}
            isParent={false}
            date={today}
            deviceHashes={isChildDevice ? deviceHashes : undefined}
            readOnly={isParentPreview}
          />
        )}

        {/* Weekly calendar — "Uka mi" */}
        {childId && (
          <ChildWeekView
            childId={childId}
            deviceHashes={isChildDevice ? deviceHashes : null}
            timezone={timezone}
            today={today}
            isParentPreview={isParentPreview}
          />
        )}

        {/* Homework section (separate, no limit) */}
        {childId && (
          <HomeworkSection
            childId={childId}
            interactive={!isParentPreview}
            isParent={false}
            deviceHashes={isChildDevice ? deviceHashes : undefined}
          />
        )}

        {/* Celebration + confetti cannon when all done */}
        {allDone && (
          <div className="flex flex-col items-center gap-3 pt-0 pb-2 mt-2">
            <p className="text-lg font-bold text-center">Du klarte alle oppgavene i dag! 🌟</p>
            <p className="text-sm text-muted-foreground text-center">⭐ {pointsToday} poeng i dag</p>
            <button
              className="mt-2 text-7xl active:scale-90 transition-transform animate-bounce hover:animate-none focus:outline-none"
              onClick={() => {
                if (isParentPreview) return;
                const side = Math.random() > 0.5;
                confetti({
                  particleCount: 80 + Math.floor(Math.random() * 80),
                  spread: 60 + Math.random() * 40,
                  angle: side ? 60 : 120,
                  origin: { x: side ? 0.1 : 0.9, y: 0.7 },
                  colors: ["#FFD700", "#FF6B6B", "#4ECDC4", "#45B7D1", "#96CEB4", "#FF69B4"],
                });
              }}
              aria-label="Fyr av konfetti"
            >
              🎉
            </button>
            <p className="text-sm text-muted-foreground font-medium">Trykk for å feire!</p>
          </div>
        )}
      </div>

      {/* Confirmation dialog before starting (not in preview) */}
      {!isParentPreview && (
        <AlertDialog
          open={!!confirmStartItem}
          onOpenChange={(open) => {
            if (!open) setConfirmStartItem(null);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Klar for å starte?</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div>
                  <span className="block font-medium text-foreground mb-2">«{confirmStartItem?.title}»</span>
                  {confirmStartItem?.description && (
                    <span className="block text-sm bg-muted rounded-lg p-2 mb-2 whitespace-pre-wrap">
                      {confirmStartItem.description}
                    </span>
                  )}
                  <span className="block">
                    ⏱️ Varighet: {confirmStartItem ? Math.round(confirmStartItem.duration / 60) : 0} minutter
                  </span>
                  <span className="block mt-2 text-xs">Timeren starter med en gang du trykker «Start nå».</span>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Ikke nå</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  if (confirmStartItem) {
                    handleStart(
                      confirmStartItem.id,
                      confirmStartItem.title,
                      confirmStartItem.duration,
                      confirmStartItem.description,
                      confirmStartItem.isRecurring,
                    );
                    setConfirmStartItem(null);
                  }
                }}
              >
                Start nå
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {/* Min fremgang drawer */}
      <Drawer open={progressOpen} onOpenChange={setProgressOpen}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>Min fremgang</DrawerTitle>
            <DrawerDescription>Se hvor mange stjerner du har samlet!</DrawerDescription>
          </DrawerHeader>
          <div className="flex flex-col items-center gap-4 px-6 pb-4">
            <div className="text-6xl">⭐</div>
            <div className="text-center">
              <p className="text-4xl font-bold">{pointsTotal}</p>
              <p className="text-muted-foreground text-sm">stjerner totalt</p>
            </div>
            <div className="w-full rounded-xl bg-accent/10 p-4 text-center space-y-1">
              <p className="text-sm font-semibold">
                Fullført i dag: {items.filter((i: any) => i.status === "done").length}
              </p>
              {allDone && items.length > 0 && <p className="text-sm">🎉 Bra jobbet!</p>}
            </div>
          </div>
          <DrawerFooter>
            <DrawerClose asChild>
              <Button variant="outline" className="w-full rounded-xl">
                Lukk
              </Button>
            </DrawerClose>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </div>
  );
}

/** Child-only timer sub-view */
function ChildTimerView({
  view,
  timer,
  setView,
  onPause,
  onComplete,
  remainingSecondsRef,
  durationSeconds,
}: {
  view: { type: "timer"; itemId: string; title: string; description?: string | null };
  timer: ReturnType<typeof useTimer>;
  setView: (v: ViewState) => void;
  onPause: (itemId: string) => void;
  onComplete: (itemId: string, title: string) => void;
  remainingSecondsRef: React.MutableRefObject<Record<string, number>>;
  durationSeconds: number;
}) {
  useEffect(() => {
    if (timer.isFinished) {
      delete remainingSecondsRef.current[view.itemId];
      setView({ type: "done", itemId: view.itemId, title: view.title });
    }
  }, [timer.isFinished, view.itemId, view.title, setView, remainingSecondsRef]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-6 gap-8">
      <h2 className="text-2xl font-display font-bold text-center">{view.title}</h2>
      {view.description && (
        <p className="text-muted-foreground bg-muted rounded-2xl p-4 max-w-xs text-center whitespace-pre-wrap">
          {view.description}
        </p>
      )}
      <CircularTimer secondsLeft={timer.secondsLeft} progress={timer.progress} />
      <div className="flex gap-3 w-full max-w-xs">
        <Button variant="outline" className="flex-1 rounded-xl py-5 font-bold" onClick={() => onPause(view.itemId)}>
          ⏸️ Pause
        </Button>
      </div>
    </div>
  );
}

/** Celebration screen with confetti */
function DoneWithConfetti({ title, onContinue }: { title: string; onContinue: () => void }) {
  useEffect(() => {
    confetti({
      particleCount: 120,
      spread: 80,
      origin: { y: 0.6 },
      colors: ["#FFD700", "#FF6B6B", "#4ECDC4", "#45B7D1", "#96CEB4"],
    });
  }, []);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-6 gap-8 animate-fade-in">
      <div className="text-8xl animate-scale-in">🏆</div>
      <h2 className="text-3xl font-display font-bold">Bra jobba!</h2>
      <p className="text-muted-foreground text-center text-lg">«{title}» er fullført! 🎉</p>
      <Button onClick={onContinue} className="w-full max-w-xs rounded-xl text-base py-6 font-bold shadow-md">
        Fortsett ➡️
      </Button>
    </div>
  );
}
